import React, { createContext, useContext, useState, useEffect } from 'react';
import { usePermissions } from '../hooks/usePermissions';

interface ProxyContextType {
  proxyRole: string | null;
  setProxyRole: (role: string | null) => void;
  isProxyActive: boolean;
  effectiveRole: string;
  availableRoles: Array<{ id: string; name: string; description: string }>;
}

const ProxyContext = createContext<ProxyContextType | undefined>(undefined);

export const useProxy = () => {
  const context = useContext(ProxyContext);
  if (context === undefined) {
    throw new Error('useProxy must be used within a ProxyProvider');
  }
  return context;
};

interface ProxyProviderProps {
  children: React.ReactNode;
}

export const ProxyProvider: React.FC<ProxyProviderProps> = ({ children }) => {
  const { hasRole, roles } = usePermissions();
  const [proxyRole, setProxyRole] = useState<string | null>(null);

  // Available roles for proxy (super users cannot be proxied)
  const availableRoles = [
    { id: 'client', name: 'client', description: 'Client-only access to their own data' },
    { id: 'user', name: 'user', description: 'Standard user with limited access' },
    { id: 'manager', name: 'manager', description: 'Campaign and client management access' },
    { id: 'admin', name: 'admin', description: 'Full system access and user management' },
  ];

  // Only allow proxy if user is super admin
  const canUseProxy = hasRole && hasRole('super') || false;

  // Effective role is either the proxy role or the user's actual highest role
  const effectiveRole = proxyRole || (roles && roles.length > 0 && roles[0]?.name ? roles[0].name : 'user');

  const isProxyActive = proxyRole !== null;

  // Clear proxy on unmount or if user loses super admin access
  useEffect(() => {
    if (!canUseProxy) {
      setProxyRole(null);
    }
  }, [canUseProxy]);

  const value: ProxyContextType = {
    proxyRole,
    setProxyRole,
    isProxyActive,
    effectiveRole,
    availableRoles: canUseProxy ? availableRoles : [],
  };

  return (
    <ProxyContext.Provider value={value}>
      {children}
    </ProxyContext.Provider>
  );
};
